import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  ACTIVE_VISIT_EVENT,
  getLastVisitsPath,
  peekActiveVisit,
  setLastVisitsPath,
  type ActiveVisitMeta,
} from '../utils/activeVisitDraft';

interface ActiveVisitContextValue extends ActiveVisitMeta {
  visitsHref: string;
  refresh: () => void;
}

const ActiveVisitContext = createContext<ActiveVisitContextValue | undefined>(
  undefined
);

export const ActiveVisitProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const [meta, setMeta] = useState<ActiveVisitMeta>({
    hasDraft: false,
    label: 'New visit',
  });
  const [visitsHref, setVisitsHref] = useState('/visits');

  const refresh = useCallback(() => {
    if (!user?.id) {
      setMeta({ hasDraft: false, label: 'New visit' });
      setVisitsHref('/visits');
      return;
    }
    setMeta(peekActiveVisit(user.id));
    setVisitsHref(getLastVisitsPath(user.id));
  }, [user?.id]);

  useEffect(() => {
    refresh();
    window.addEventListener(ACTIVE_VISIT_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(ACTIVE_VISIT_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!user?.id) return;
    if (location.pathname === '/visits' || location.pathname === '/visits/new') {
      setLastVisitsPath(user.id, location.pathname);
      setVisitsHref(location.pathname);
    }
  }, [location.pathname, user?.id]);

  return (
    <ActiveVisitContext.Provider
      value={{ ...meta, visitsHref, refresh }}
    >
      {children}
    </ActiveVisitContext.Provider>
  );
};

export function useActiveVisit(): ActiveVisitContextValue {
  const ctx = useContext(ActiveVisitContext);
  if (!ctx) {
    return {
      hasDraft: false,
      label: 'New visit',
      visitsHref: '/visits',
      refresh: () => undefined,
    };
  }
  return ctx;
}
